import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Slider,
  InputAdornment
} from '@mui/material';
import { getMarqueeMessage, updateMarqueeMessage } from '../../models/MarqueeMessage';

/**
 * Composant pour gérer le message défilant dans la navbar
 */
const MarqueeSettings = () => {
  const [marqueeData, setMarqueeData] = useState({
    message: '',
    speed: 30
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Charger le message défilant actuel
  useEffect(() => {
    const fetchMarqueeMessage = async () => {
      try {
        setLoading(true);
        const data = await getMarqueeMessage();
        setMarqueeData(data);
      } catch (err) {
        console.error('Erreur lors du chargement du message défilant:', err);
        setError('Impossible de charger le message défilant. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMarqueeMessage();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMarqueeData(prev => ({ ...prev, [name]: value }));
  };

  const handleSpeedChange = (event, newValue) => {
    setMarqueeData(prev => ({ ...prev, speed: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!marqueeData.message.trim()) {
      setError('Le message ne peut pas être vide.');
      return;
    }
    
    try {
      setSaving(true);
      const success = await updateMarqueeMessage(marqueeData.message, marqueeData.speed);
      
      if (success) {
        setSuccessMessage('Le message défilant a été mis à jour avec succès.');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error('Échec de la mise à jour du message.');
      }
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Une erreur est survenue lors de la sauvegarde. ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#014527', fontWeight: 'bold' }}>
        Message défilant de la page d'accueil
      </Typography>
      
      <Paper sx={{ p: 3, borderRadius: '10px', maxWidth: '100%' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Message défilant"
            name="message"
            value={marqueeData.message}
            onChange={handleChange}
            margin="normal"
            required
            multiline
            rows={3}
            helperText="Ce message s'affichera en haut de la page d'accueil. Vous pouvez utiliser des émojis (✅, 🌱, etc.)"
          />
          
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>Vitesse de défilement (secondes)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Slider
                value={marqueeData.speed}
                onChange={handleSpeedChange}
                aria-labelledby="marquee-speed-slider"
                valueLabelDisplay="auto"
                step={5}
                marks
                min={20}
                max={60}
                sx={{ mr: 2, flexGrow: 1 }}
              />
              <TextField
                value={marqueeData.speed}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 20 && value <= 60) {
                    setMarqueeData(prev => ({ ...prev, speed: value }));
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end" sx={{ 
                    whiteSpace: 'nowrap',
                    '& p': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                  }}>sec</InputAdornment>,
                }}
                inputProps={{
                  step: 5,
                  min: 20,
                  max: 60,
                  type: 'number',
                }}
                sx={{ 
                  width: { xs: '120px', sm: '100px' },
                  '& .MuiInputBase-root': {
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: '0.25rem', sm: '0' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Une valeur plus élevée = défilement plus lent (plus facile à lire)
            </Typography>
          </Box>
          
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{
              mt: 3,
              bgcolor: '#014527',
              '&:hover': { bgcolor: '#013320' }
            }}
          >
            {saving ? <CircularProgress size={24} /> : 'Mettre à jour le message'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default MarqueeSettings;